// Suspend my code: after a confirm dialog and step-up "suspend", the server
// ends every session and clears the cookies, so the session is cleared here
// too (by SuspendedPage) and the person lands on /suspended.
import { Button, Group, Modal, Paper, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { useStepUp } from "../../shared/session/stepup";
import { FormError } from "../../shared/ui/Page";

export function SuspendSection() {
  const { t } = useTranslation();
  const stepUp = useStepUp();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suspend = async () => {
    setConfirming(false);
    setError(null);
    const token = await stepUp("suspend");
    if (!token) return;
    const { response, error } = await api.POST("/me/suspend", { params: { header: { "X-Step-Up-Token": token } } });
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    // The router navigates in a transition, so /suspended clears the session
    // once it has rendered; clearing here would bounce this page to /signin.
    navigate("/suspended", { replace: true, state: { signOut: true } });
  };

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      component="section"
      aria-labelledby="danger-title"
      style={{ borderColor: "var(--mantine-color-red-6)" }}
    >
      <Stack gap="sm">
        <Title order={2} size="h4" id="danger-title">
          {t("suspend.danger_title")}
        </Title>
        <Text size="sm" fw={600}>
          {t("suspend.title")}
        </Text>
        <Text size="sm">{t("suspend.intro")}</Text>
        <Button color="red" variant="outline" w="fit-content" onClick={() => setConfirming(true)}>
          {t("suspend.button")}
        </Button>
        <FormError message={error} />
      </Stack>
      <Modal
        opened={confirming}
        onClose={() => setConfirming(false)}
        title={t("suspend.modal_title")}
        centered
        closeButtonProps={{ "aria-label": t("common.close") }}
      >
        <Stack gap="sm" data-testid="suspend-confirm">
          <Text size="sm">{t("suspend.modal_body")}</Text>
          <Text size="sm">{t("suspend.modal_support")}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
            <Button color="red" onClick={() => void suspend()}>
              {t("suspend.confirm")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
